import clsx from "clsx";
import "./CardDocument.scss";
import { Button, Col, Loader, Row } from "@/components";
import { Divider } from "@/components/ui/Divider";
import { ComponentType } from "react";
import { Download, Favourite, Obscured } from "../../../icons";

type CardDocumentProps = {
  eventNumber?: string;
  date?: string | number;
  documentType?: string;
  documentTitle: string;
  label?: string;
  subtitle?: string;
  showFavorite?: boolean;
  favoriteLabel?: string;
  handleFavoriteClick?: () => void;
  action?: string;
  CTA?: string;
  iconView?: "attachments" | "obscured";
  favorite?: boolean;
  handleDownloadDocument?: () => void;
  hrefDocument?: string;
  isLoading?: boolean;
  openDocumentLabel?: string;
  releaseDateLabel?: string;
  eventLabel?: string;
  asLink?: "a" | ComponentType<any>;
  showBottomActions?: boolean;
  showIsHidden?: boolean;
  isLoadingVisibility?: boolean;
  obscuredLabel?: string;
};

export const CardDocument = ({
  eventNumber,
  date = "--/--/--",
  documentType,
  showBottomActions = true,
  showFavorite = false,
  favorite = false,
  favoriteLabel = "Preferito",
  handleFavoriteClick,
  showIsHidden = false,
  documentTitle,
  label,
  subtitle,
  action,
  CTA,
  handleDownloadDocument,
  hrefDocument = "#",
  isLoading = false,
  openDocumentLabel = "Apri documento",
  releaseDateLabel = "Inserito il",
  eventLabel = "Evento n.",
  asLink = "a",
  isLoadingVisibility = false,
  obscuredLabel = "Oscurato",
}: CardDocumentProps) => {
  const LinkComponent = asLink;

  const renderDownloadAction = () => {
    if (!action) return null;
    return (
      <Row
        alignItems={"center"}
        className={clsx(
          !isLoading && "cursor-pointer",
          "text-decoration-none text-primary",
          "ui-document-download-row",
        )}
        onClick={isLoading ? undefined : handleDownloadDocument}
      >
        <Download />
        <p>{action}</p>
        <Loader loading={isLoading} className={"ml-x8"} />
      </Row>
    );
  };

  return (
    <div
      className={clsx(
        "ui-card-document shadow br-x4 fw-semibold text-slate-28 fs-7",
      )}
    >
      <Row
        max-h={"x40"}
        min-h={"x40"}
        as={"header"}
        justifyContent={
          eventNumber || showFavorite ? "space-between" : "flex-end"
        }
        alignItems={"center"}
        px={"x24"}
        className={"ui-document-header"}
      >
        {eventNumber && (
          <Row>{eventNumber ? `${eventLabel} ${eventNumber}` : ""}</Row>
        )}

        {showFavorite && (
          <Row alignItems={"center"} gap={"x8"}>
            <Favourite
              filled={favorite}
              color={"secondary"}
              onClick={handleFavoriteClick}
            />
            {favorite && <div>{favoriteLabel}</div>}
          </Row>
        )}

        <Row className={"fw-semibold fs-7"}>
          {releaseDateLabel} {date}
        </Row>
      </Row>
      <main className={"py-x16 px-x24"}>
        <Row justifyContent={"space-between"}>
          <Col
            pb={showBottomActions ? "x32" : undefined}
            justifyContent={"space-between"}
          >
            <div>
              <Row className={"fw-semibold"} pb={"x8"}>
                {documentType}
              </Row>
              <h3 className={"fs-4 text-slate-20 cursor-pointer hover-underline"}>
                <LinkComponent className="link-unstyled" href={hrefDocument}>
                  {documentTitle}
                </LinkComponent>
              </h3>
              {label && (
                <Row pt={"x8"}>
                  <div
                    className={"bg-secondary text-white rounded py-x4 px-x16"}
                    style={{ width: "max-content" }}
                  >
                    {label}
                  </div>
                </Row>
              )}
            </div>
            {subtitle && <p className={"fw-regular fs-6 pt-x8"}>{subtitle}</p>}
          </Col>
          {isLoadingVisibility ? (
            <Loader loading={isLoadingVisibility} className={"m-x16"} />
          ) : (
            showIsHidden && (
              <Col gap={"x4"} alignItems={"center"}>
                <Obscured />
                <div className={"fs-7 fw-semibold"}>{obscuredLabel}</div>
              </Col>
            )
          )}
        </Row>
        {showBottomActions && (
          <Col mt={"x16"} className="ui-document-footer">
            <div className="ui-document-footer-actions">
              {renderDownloadAction()}
              <Divider bg={"border-divider"} className="ui-document-divider" />
              {CTA && (
                <Button
                  fwClassName={"fw-semibold"}
                  className="ui-document-cta"
                  variant={"secondary"}
                  size="5"
                  asLink={LinkComponent}
                  href={hrefDocument}
                >
                  {CTA}
                </Button>
              )}
              {!CTA && (
                <Button
                  fwClassName={"fw-semibold"}
                  className="ui-document-open-cta"
                  variant={"secondary"}
                  size="4"
                  asLink={LinkComponent}
                  href={hrefDocument}
                >
                  {openDocumentLabel}
                </Button>
              )}
            </div>
          </Col>
        )}
      </main>
    </div>
  );
};
